import React from "react";
import Tooltip from "components/Tooltip";
import { ROUTES_ENUM } from "routes/consts";
import { searchStore } from "features/ProductSearch/SearchStore";
import useFeatureFlag from "hooks/useFeatureFlag";
import { useNavigate } from "react-router";
import { IconCancel, IconCopy, IconEdit, IconWorld } from "@tabler/icons-react";
import clsx from "clsx";
import { useTranslation } from "react-i18next";
import { observer } from "mobx-react-lite";
import Button from "components/Button";
import useProductActions from "features/AdvancedProductSearch/useProductActions";
import { ProductActionMode } from "utils";

const PoductHeaderActions = ({
  productId,
  isComponent,
  canEdit,
  canDuplicate,
  isOwnedByOrg,
  isManufacturerLicence,
  isManufacturerProduct,
  isPublic,
  changeStatus,
}: {
  productId?: string | null;
  isComponent?: boolean;
  canEdit?: boolean;
  canDuplicate?: boolean;
  isOwnedByOrg?: boolean;
  isManufacturerLicence?: boolean;
  isManufacturerProduct?: boolean;
  productName?: string | null;
  isPublic?: boolean;
  changeStatus?: () => Promise<void>;
}) => {
  const { isFeatureVisible } = useFeatureFlag();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { editComponent, duplicateComponent } = useProductActions();

  const showEditDuplicateButtons =
    !searchStore.openProductSearch.state &&
    !searchStore.usingOrganisationSearch &&
    !location.pathname.includes("component-editor") &&
    isFeatureVisible("product_details_edit_duplicate") &&
    !isManufacturerProduct;

  const openEditProduct = () => {
    if (isComponent) {
      editComponent(productId, true);
    } else {
      navigate(`./${ROUTES_ENUM.PRODUCT_EDITOR}`, {
        state: {
          id: productId,
          compliant: false,
          mode: ProductActionMode.Edit,
          isDetails: true,
        },
      });
    }
  };

  const openDuplicateProduct = () => {
    if (isComponent) {
      duplicateComponent(productId, true);
    } else {
      navigate(ROUTES_ENUM.PRODUCT_EDITOR, {
        state: {
          id: productId,
          compliant: false,
          mode: ProductActionMode.Duplicate,
          isDetails: true,
        },
      });
    }
  };

  function getEditTooltip(): React.ReactNode {
    if (!canEdit) return t("productSearch.editNoPermission");
    return isComponent ? t("mapping.editComponent") : t("mapping.editProduct");
  }

  if (isManufacturerLicence)
    return (
      <Button
        className="mr-2 !h-[36px]"
        leadingIcon={isPublic ? <IconCancel /> : <IconWorld />}
        onClick={changeStatus}
      >
        {isPublic ? t("productSearch.unpublish") : t("productSearch.publish")}
      </Button>
    );

  if (!showEditDuplicateButtons) return null;

  return (
    <>
      <div className="justify-between items-center text-indigo-700 flex">
        {isOwnedByOrg && (
          <Tooltip content={getEditTooltip()}>
            <IconEdit
              width={24}
              className={clsx("mr-2", {
                "cursor-pointer": canEdit,
                "cursor-not-allowed opacity-50": !canEdit,
              })}
              stroke={2}
              onClick={canEdit ? openEditProduct : undefined}
            />
          </Tooltip>
        )}
        {canDuplicate && (
          <Tooltip
            content={
              isComponent
                ? t("mapping.duplicateComponent")
                : t("mapping.duplicateProduct")
            }
          >
            <IconCopy
              width={24}
              className="mr-2 cursor-pointer"
              stroke={2}
              onClick={openDuplicateProduct}
            />
          </Tooltip>
        )}
      </div>
    </>
  );
};

export default observer(PoductHeaderActions);
